/**
 * API client for the AI Assistant
 */
import { conns } from '@cfg/axiosConfig.tsx';
import type {
  SessionStartRequest,
  SessionStartResponse,
  MessageRequest,
  MessageResponse,
  AssistantHistoryResponse,
  AvailableActionsResponse,
  AssistantCapabilitiesRequest,
  AssistantCapabilitiesResponse,
  AssistantFeedbackRequest,
  AssistantFeedbackResponse,
  AssistantSupportResponse,
  AssistantActionResolution,
  AssistantSettingsResponse,
  AssistantMetricsResponse,
  AssistantConversationPageResponse,
  AssistantConversationDetailResponse,
} from '@app-types/assistant.ts';

export const getCapabilities = async (
  data: AssistantCapabilitiesRequest
): Promise<AssistantCapabilitiesResponse> => {
  const response = await conns<AssistantCapabilitiesResponse>(
    'assistant:capabilities'
  ).post(data);
  return response.data;
};

/**
 * Start a new assistant chat session
 * @param data Session start request data
 * @returns Promise with session_id
 */
export const startSession = async (
  data: SessionStartRequest = { version: 2, mode: 'resume_or_create' }
): Promise<SessionStartResponse> => {
  const response = await conns<SessionStartResponse>(
    'assistant:session:start'
  ).post(data);
  return response.data;
};

/**
 * Send a message to the assistant
 * @param sessionId Chat session ID
 * @param data Message request data
 * @returns Promise with assistant's response
 */
export const sendMessage = async (
  sessionId: number,
  data: MessageRequest
): Promise<MessageResponse> => {
  const response = await conns<MessageResponse>('assistant:session:message', [
    sessionId,
    'message',
  ]).post(data);
  return response.data;
};

/**
 * Get chat history for a session
 * @param sessionId Chat session ID
 * @param limit Maximum number of messages to return
 * @param offset Number of messages to skip
 * @returns Promise with list of chat messages
 */
export const getHistory = async (
  sessionId: number,
  limit: number = 50,
  offset: number = 0
): Promise<AssistantHistoryResponse> => {
  const response = await conns<AssistantHistoryResponse>(
    'assistant:session:history',
    [sessionId, 'history']
  ).get({ version: 2, limit, offset });
  return response.data;
};

export const endSession = async (
  sessionId: number
): Promise<SessionStartResponse> => {
  const response = await conns<SessionStartResponse>('assistant:session:end', [
    sessionId,
    'end',
  ]).post({ version: 2 });
  return response.data;
};

export const submitFeedback = async (
  sessionId: number,
  messageId: number,
  data: AssistantFeedbackRequest
): Promise<AssistantFeedbackResponse> => {
  const response = await conns<AssistantFeedbackResponse>(
    'assistant:session:feedback',
    [sessionId, 'messages', messageId, 'feedback']
  ).put(data);
  return response.data;
};

export const getSupport = async (): Promise<AssistantSupportResponse> => {
  const response = await conns<AssistantSupportResponse>('assistant:support').get({
    version: 2,
  });
  return response.data;
};

export const resolveAction = async (
  sessionId: number,
  messageId: number,
  actionId: string,
  conversationRevision: number
): Promise<AssistantActionResolution> => {
  const response = await conns<AssistantActionResolution>(
    'assistant:actions:resolve',
    [sessionId, 'messages', messageId, 'actions', actionId, 'resolve']
  ).post({ version: 2, conversation_revision: conversationRevision });
  return response.data;
};

/**
 * Get list of actions available to the current user
 * @returns Promise with list of available actions
 */
export const getAvailableActions =
  async (): Promise<AvailableActionsResponse> => {
    const response = await conns<AvailableActionsResponse>(
      'assistant:actions:available'
    ).get();
    return response.data;
  };

export const getAssistantSettings = async (): Promise<AssistantSettingsResponse> => {
  const response = await conns<AssistantSettingsResponse>('assistant:settings').get();
  return response.data;
};

export const patchAssistantSettings = async (
  overrides: AssistantSettingsResponse['overrides'],
): Promise<AssistantSettingsResponse> => {
  const response = await conns<AssistantSettingsResponse>('assistant:settings').patch({
    version: 2,
    overrides,
  });
  return response.data;
};

export const getAssistantMetrics = async (): Promise<AssistantMetricsResponse> => {
  const response = await conns<AssistantMetricsResponse>('assistant:audit:metrics').get();
  return response.data;
};

export const getAssistantConversations = async (
  cursor?: string | null,
): Promise<AssistantConversationPageResponse> => {
  const response = await conns<AssistantConversationPageResponse>(
    'assistant:audit:conversations',
  ).get({ limit: 25, ...(cursor ? { cursor } : {}) });
  return response.data;
};

export const getAssistantConversation = async (
  sessionId: number,
): Promise<AssistantConversationDetailResponse> => {
  const response = await conns<AssistantConversationDetailResponse>(
    'assistant:audit:conversations',
    [sessionId],
  ).get();
  return response.data;
};
